import React, { useState, useEffect, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useNavigate } from 'react-router-dom';
import realUserService from '../../../services/userService';

const UserManagementTable = ({ users, loading, onEditUser, onDeleteUser, onResendInvitation, onBulkAction, onRefresh }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [artistTypeFilter, setArtistTypeFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [passwordModalUser, setPasswordModalUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [generatePassword, setGeneratePassword] = useState(true);
  const [passwordResult, setPasswordResult] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [artistTypeModalUser, setArtistTypeModalUser] = useState(null);
  const [newArtistType, setNewArtistType] = useState('');
  const [artistTypeLoading, setArtistTypeLoading] = useState(false);

  // Force dark mode for admin components
  useEffect(() => {
    document.documentElement?.setAttribute('data-theme', 'dark');
  }, []);

  const roleOptions = [
    { value: 'all', label: 'All Roles' },
    { value: 'admin', label: 'Admin' },
    { value: 'editor', label: 'Editor' },
    { value: 'artist', label: 'Artist' }
  ];

  const artistTypeOptions = [
    { value: 'all', label: 'All Artist Types' },
    { value: 'musician', label: 'Musician' },
    { value: 'visual', label: 'Visual' },
    { value: 'director', label: 'Director' },
    { value: 'actor', label: 'Actor' },
    { value: 'writer', label: 'Writer' }
  ];

  const bulkActionOptions = [
    { value: 'activate', label: 'Activate Selected' },
    { value: 'deactivate', label: 'Deactivate Selected' },
    { value: 'delete', label: 'Delete Selected' }
  ];

  const handleArtistTypeChange = async (user) => {
    try {
      setArtistTypeLoading(true);
      setError(null);
      
      if (!newArtistType) {
        throw new Error('Please select an artist type');
      }

      const { default: userService } = await import('../../../services/userService');
      
      let result = await userService?.updateUser(user?.id, {
        artist_type: newArtistType
      });

      setSuccessMessage(`Artist type changed successfully for ${user?.full_name}`);
      
      // Reload users to show updated data
      onRefresh?.();
      
      // Close modal after short delay
      setTimeout(() => {
        setArtistTypeModalUser(null);
        setNewArtistType('');
        setSuccessMessage(null);
      }, 2000);

    } catch (error) {
      setError(error?.message || 'Failed to update artist type');
    } finally {
      setArtistTypeLoading(false);
    }
  };

  const handlePasswordChange = async (user) => {
    try {
      setPasswordLoading(true);
      setError(null);
      
      let result;
      if (generatePassword) {
        result = await realUserService?.resetUserPassword(user?.email, true);
      } else {
        if (!newPassword || newPassword?.length < 6) {
          throw new Error('Password must be at least 6 characters long');
        }
        result = await realUserService?.updateUserPassword(user?.email, newPassword);
      }

      setPasswordResult(result);
      setSuccessMessage(`Password ${generatePassword ? 'reset' : 'updated'} successfully for ${user?.email}`);
      
      // Close modal after short delay
      setTimeout(() => {
        setPasswordModalUser(null);
        setNewPassword('');
        setPasswordResult(null);
      }, 3000);

    } catch (error) {
      setError(error?.message);
    } finally {
      setPasswordLoading(false);
    }
  };

  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users?.filter(user => {
      const matchesSearch = user?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                           user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      const matchesRole = roleFilter === 'all' || user?.role === roleFilter;
      const matchesArtistType = artistTypeFilter === 'all' || user?.artist_type === artistTypeFilter;
      
      return matchesSearch && matchesRole && matchesArtistType;
    });

    if (sortConfig?.key) {
      filtered?.sort((a, b) => {
        // Map 'name' to 'full_name' for proper sorting
        const sortKey = sortConfig?.key === 'name' ? 'full_name' : sortConfig?.key;
        let aValue = a?.[sortKey];
        let bValue = b?.[sortKey];
        
        if (sortConfig?.key === 'created_at') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }
        
        if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [users, searchTerm, roleFilter, artistTypeFilter, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedUsers(filteredAndSortedUsers?.map(user => user?.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId, checked) => {
    if (checked) {
      setSelectedUsers(prev => [...prev, userId]);
    } else {
      setSelectedUsers(prev => prev?.filter(id => id !== userId));
    }
  };

  const handleBulkAction = (action) => {
    if (selectedUsers?.length > 0) {
      onBulkAction(action, selectedUsers);
      setSelectedUsers([]);
    }
  };

  const handleRowClick = (user, event) => {
    // Prevent navigation if clicking on interactive elements
    if (event?.target?.closest('button, input[type="checkbox"], a')) {
      return;
    }
    
    // Only navigate if user is an artist
    if (user?.role === 'artist') {
      navigate(`/artist-detail-admin-view?artistId=${user?.id}`);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-success text-success-foreground', label: 'Active' },
      inactive: { color: 'bg-warning text-warning-foreground', label: 'Inactive' },
      pending: { color: 'bg-accent text-accent-foreground', label: 'Pending' },
      suspended: { color: 'bg-destructive text-destructive-foreground', label: 'Suspended' }
    };
    
    const config = statusConfig?.[status] || statusConfig?.pending;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ArrowUpDown" size={14} className="text-text-secondary" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ArrowUp" size={14} className="text-primary" />
      : <Icon name="ArrowDown" size={14} className="text-primary" />;
  };

  // Show loading state
  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-12 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-text-secondary">Loading users...</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border elevation-1">
      {/* Filters and Search */}
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="flex-1 max-w-md">
              <Input
                type="search"
                placeholder="Search users by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full"
              />
            </div>
            <div className="flex gap-3">
              <Select
                options={roleOptions}
                value={roleFilter}
                onChange={setRoleFilter}
                placeholder="Filter by role"
                className="w-40"
              />
              <Select
                options={artistTypeOptions}
                value={artistTypeFilter}
                onChange={setArtistTypeFilter}
                placeholder="Filter by type"
                className="w-40"
              />
            </div>
          </div>
          
          {selectedUsers?.length > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-text-secondary">
                {selectedUsers?.length} selected
              </span>
              <Select
                options={bulkActionOptions}
                onChange={handleBulkAction}
                placeholder="Bulk actions"
                className="w-40"
              />
            </div>
          )}
        </div>
      </div>
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="p-4 text-left">
                <Checkbox
                  checked={selectedUsers?.length === filteredAndSortedUsers?.length && filteredAndSortedUsers?.length > 0}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                />
              </th>
              <th 
                className="p-4 text-left cursor-pointer hover:bg-muted-foreground/5 transition-colors"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-text-primary">Name</span>
                  {getSortIcon('name')}
                </div>
              </th>
              <th 
                className="p-4 text-left cursor-pointer hover:bg-muted-foreground/5 transition-colors"
                onClick={() => handleSort('email')}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-text-primary">Email</span>
                  {getSortIcon('email')}
                </div>
              </th>
              <th 
                className="p-4 text-left cursor-pointer hover:bg-muted-foreground/5 transition-colors"
                onClick={() => handleSort('role')}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-text-primary">Role</span>
                  {getSortIcon('role')}
                </div>
              </th>
              <th className="p-4 text-left">
                <span className="text-sm font-medium text-text-primary">Artist Type</span>
              </th>
              <th 
                className="p-4 text-left cursor-pointer hover:bg-muted-foreground/5 transition-colors"
                onClick={() => handleSort('created_at')}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-text-primary">Created</span>
                  {getSortIcon('created_at')}
                </div>
              </th>
              <th className="p-4 text-left">
                <span className="text-sm font-medium text-text-primary">Status</span>
              </th>
              <th className="p-4 text-left">
                <span className="text-sm font-medium text-text-primary">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedUsers?.map((user) => (
              <tr 
                key={user?.id} 
                className={`border-b border-border transition-colors ${
                  user?.role === 'artist' ? 'hover:bg-accent/20 cursor-pointer' : 'hover:bg-muted/50'
                }`}
                onClick={(e) => handleRowClick(user, e)}
                title={user?.role === 'artist' ? 'Click to view artist details' : ''}
              >
                <td className="p-4" onClick={(e) => e?.stopPropagation()}>
                  <Checkbox
                    checked={selectedUsers?.includes(user?.id)}
                    onChange={(e) => handleSelectUser(user?.id, e?.target?.checked)}
                  />
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-primary-foreground">
                        {user?.full_name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-text-primary">{user?.full_name}</span>
                      {user?.role === 'artist' && (
                        <Icon name="ExternalLink" size={14} className="text-primary" />
                      )}
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <span className="text-sm text-text-secondary">{user?.email}</span>
                </td>
                <td className="p-4">
                  <span className="text-sm font-medium text-text-primary capitalize">{user?.role}</span>
                </td>
                <td className="p-4">
                  <span className="text-sm text-text-secondary capitalize">
                    {user?.artist_type || '-'}
                  </span>
                </td>
                <td className="p-4">
                  <span className="text-sm text-text-secondary">
                    {new Date(user?.created_at)?.toLocaleDateString()}
                  </span>
                </td>
                <td className="p-4">
                  {getStatusBadge(user?.status)}
                </td>
                <td className="p-4" onClick={(e) => e?.stopPropagation()}>
                  <div className="flex items-center gap-2">
                    {user?.role === 'artist' && (
                      <button
                        onClick={() => {
                          setArtistTypeModalUser(user);
                          setNewArtistType(user?.artist_type || '');
                        }}
                        className="text-purple-600 hover:text-purple-900 text-sm font-medium"
                        title="Change Artist Type"
                      >
                        🎨 Type
                      </button>
                    )}
                    <button
                      onClick={() => setPasswordModalUser(user)}
                      className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                      title="Change Password"
                    >
                      🔑 Password
                    </button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEditUser(user)}
                      iconName="Edit"
                      className="h-8 w-8 hover:bg-accent"
                      title="Edit user"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onResendInvitation(user)}
                      iconName="Mail"
                      className="h-8 w-8 hover:bg-accent"
                      title="Resend invitation"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDeleteUser(user)}
                      iconName="Trash2"
                      className="h-8 w-8 text-destructive hover:text-destructive hover:bg-red-50"
                      title="Delete user"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden p-4 space-y-4">
        {filteredAndSortedUsers?.map((user) => (
          <div 
            key={user?.id} 
            className={`bg-surface border border-border rounded-lg p-4 ${
              user?.role === 'artist' ? 'cursor-pointer hover:border-primary' : ''
            }`}
            onClick={(e) => handleRowClick(user, e)}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div onClick={(e) => e?.stopPropagation()}>
                  <Checkbox
                    checked={selectedUsers?.includes(user?.id)}
                    onChange={(e) => handleSelectUser(user?.id, e?.target?.checked)}
                  />
                </div>
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-primary-foreground">
                    {user?.full_name?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-text-primary">{user?.full_name}</h3>
                    {user?.role === 'artist' && (
                      <Icon name="ExternalLink" size={12} className="text-primary" />
                    )}
                  </div>
                  <p className="text-xs text-text-secondary">{user?.email}</p>
                </div>
              </div>
              {getStatusBadge(user?.status)}
            </div>
            
            <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
              <div>
                <span className="text-text-secondary">Role:</span>
                <span className="ml-1 font-medium text-text-primary capitalize">{user?.role}</span>
              </div>
              <div>
                <span className="text-text-secondary">Type:</span>
                <span className="ml-1 text-text-primary capitalize">{user?.artist_type || '-'}</span>
              </div>
              <div className="col-span-2">
                <span className="text-text-secondary">Created:</span>
                <span className="ml-1 text-text-primary">
                  {new Date(user?.created_at)?.toLocaleDateString()}
                </span>
              </div>
            </div>
            
            <div className="flex items-center gap-2 pt-3 border-t border-border" onClick={(e) => e?.stopPropagation()}>
              {user?.role === 'artist' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setArtistTypeModalUser(user);
                    setNewArtistType(user?.artist_type || '');
                  }}
                  iconName="Palette"
                  iconPosition="left"
                  iconSize={14}
                >
                  Type
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEditUser(user)}
                iconName="Edit"
                iconPosition="left"
                iconSize={14}
              >
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onResendInvitation(user)}
                iconName="Mail"
                iconPosition="left"
                iconSize={14}
              >
                Resend
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDeleteUser(user)}
                iconName="Trash2"
                iconPosition="left"
                iconSize={14}
                className="text-destructive hover:text-destructive"
              >
                Delete
              </Button>
            </div>
          </div>
        ))}
      </div>
      {/* Empty State */}
      {filteredAndSortedUsers?.length === 0 && !loading && (
        <div className="p-12 text-center">
          <Icon name="Users" size={48} className="text-text-secondary mx-auto mb-4" />
          <h3 className="text-lg font-medium text-text-primary mb-2">No users found</h3>
          <p className="text-text-secondary mb-4">
            {searchTerm || roleFilter !== 'all' || artistTypeFilter !== 'all' ? 'Try adjusting your search or filter criteria.' : 'Get started by adding your first user.'}
          </p>
          {onRefresh && (
            <Button
              variant="outline"
              onClick={onRefresh}
              iconName="RefreshCw"
              iconPosition="left"
            >
              Refresh
            </Button>
          )}
        </div>
      )}
      {/* Artist Type Change Modal */}
      {artistTypeModalUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-card rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-text-primary">
                  Change Artist Type
                </h3>
                <button
                  onClick={() => {
                    setArtistTypeModalUser(null);
                    setNewArtistType('');
                    setError(null);
                    setSuccessMessage(null);
                  }}
                  className="p-2 hover:bg-muted rounded-lg transition-colors"
                  disabled={artistTypeLoading}
                >
                  <Icon name="X" size={20} className="text-text-secondary" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm text-text-secondary mb-2">
                    Change artist type for <strong>{artistTypeModalUser?.full_name}</strong>
                  </p>
                  <p className="text-xs text-text-secondary">
                    Current type: <span className="font-medium capitalize">{artistTypeModalUser?.artist_type || 'Not set'}</span>
                  </p>
                </div>

                <div>
                  <Select
                    label="New Artist Type"
                    options={artistTypeOptions?.filter(opt => opt?.value !== 'all')?.map(opt => ({
                      ...opt,
                      label: opt?.label === 'Musician' ? '🎵 Musician' :
                             opt?.label === 'Visual' ? '🎨 Visual Artist' :
                             opt?.label === 'Director' ? '🎬 Director' :
                             opt?.label === 'Actor' ? '🎭 Actor' :
                             opt?.label === 'Writer' ? '✍️ Writer' :
                             opt?.label
                    }))}
                    value={newArtistType}
                    onChange={setNewArtistType}
                    placeholder="Select new artist type"
                    required
                    disabled={artistTypeLoading}
                  />
                </div>

                {error && (
                  <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Icon name="AlertTriangle" size={16} className="text-destructive" />
                      <p className="text-sm text-destructive">{error}</p>
                    </div>
                  </div>
                )}

                {successMessage && (
                  <div className="p-3 bg-success/10 border border-success/20 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Icon name="CheckCircle" size={16} className="text-success" />
                      <p className="text-sm text-success">{successMessage}</p>
                    </div>
                  </div>
                )}

                <div className="flex gap-3 justify-end pt-4 border-t border-border">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setArtistTypeModalUser(null);
                      setNewArtistType('');
                      setError(null);
                      setSuccessMessage(null);
                    }}
                    disabled={artistTypeLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={() => handleArtistTypeChange(artistTypeModalUser)}
                    loading={artistTypeLoading}
                    disabled={artistTypeLoading || !newArtistType}
                    iconName="Palette"
                    iconPosition="left"
                  >
                    Update Type
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementTable;